import * as Sentry from "@sentry/react";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { notifyManager, type QueryClient } from "@tanstack/react-query";
import type {
  PersistedClient,
  Persister,
} from "@tanstack/react-query-persist-client";
import { z } from "zod";
import { queryClient } from "../queryClient";

/**
 * Routes whose body must never live in localStorage. Persisting a body outlives
 * the bundle that minted it, so the next deploy rehydrates the OLD shape under
 * NEW code — a failure types cannot catch, since the bad data predates the
 * types. See `.knowledge/react-query-persisted-stale-shape.md`.
 */
export const NEVER_PERSISTED_ROUTES: ReadonlySet<unknown> = new Set([
  // The visual abstract view is reshaped constantly and both its routes refetch
  // on mount anyway (`staleTime: 0` on the preview), so persisting them buys
  // nothing and has already crashed `HeroBento` twice.
  "/webapp/public/visual-abstracts/:token",
  "/webapp/private/visual-abstracts/:id",
  // A conversation share changes at each share and stop: a rehydrated body would
  // show an older snapshot, or a link its owner stopped, before the refetch.
  "/webapp/public/conversations/shared/:token",
  // The watcher's status poll. A job status is only ever worth what the server
  // said a moment ago, and rehydrating one from the last session would have the
  // app reasoning about a run that finished days earlier.
  "/webapp/private/visual-abstracts",
  // The practice answer, for the same reason and a sharper consequence. It is
  // read by a REDIRECT (`usePracticeProfilingGate` → `/onboarding/practice`), and
  // a rehydrated body renders as `state: "success"` on the very first frame —
  // before the refetch it triggers can land. A `confirmedAt: null` persisted
  // before the practitioner answered therefore fires the gate on boot and asks
  // them again, and no later re-render takes a redirect back.
  "/webapp/private/practice-profiles/me",
  // A Specialty's catalogue, for the same reason: an empty one sends the reader
  // off `/onboarding/practice` (AppRoutes), and a body persisted before the
  // catalogue shipped would do it on the first frame.
  "/webapp/public/practice-profiles/offered",
  // The logged-out JuisciGPT allowance decides on the first frame whether a
  // composer is offered: a persisted `open` would offer one to a browser the
  // API now answers `member` for, until the refetch lands.
  "/webapp/public/gpt/allowance",
  // The signup profession picker lists only `onboardingVisibility` main/more,
  // on the first frame. A body persisted before that field existed would list
  // nothing until the refetch landed; the list is small and cheap to refetch.
  "/webapp/public/professions",
  // A run's frozen config, read with `staleTime: Infinity`: a body persisted before
  // `reasoningEffortByRole` existed would crash the debug Config tab and never refetch.
  "/assistant/conversations/:conversationId/runs/:runId/debug/config",
  // A run's trace refetches on open (`staleTime: 0`): a body persisted before
  // `purpose` existed would crash the trace on its first frame.
  "/assistant/conversations/:conversationId/runs/:runId/debug",
  // The playground dossiers change with the catalog: a body persisted before a
  // change lacks a dossier that Gallery reads, and Gallery throws on its first frame.
  "/assistant/genui/dossiers",
]);

/**
 * Enforce that set in BOTH directions: what this bundle writes, and what any
 * earlier one left behind.
 *
 * Filtering the dehydrate side alone — which is all this used to do — stops the
 * writing and nothing else. A body an older bundle already wrote is still in
 * storage, and restore hydrates it into the cache one frame before anything can
 * refetch it; for the practice answer that is one frame before the gate's
 * redirect, and a redirect does not come back. Bumping the cache buster would
 * also clear it, at the price of dropping every OTHER persisted body for every
 * user, once, for a rule that names four routes.
 *
 * Dropped on RESTORE rather than after it, so it never enters the cache at all:
 * `PersistQueryClientProvider` runs its `onSuccess` before it lowers
 * `isRestoring`, but "no reader has observed it yet" is an ordering argument, and
 * this needs to be a structural one.
 */
export function withoutNeverPersistedRoutes(inner: Persister): Persister {
  return {
    ...inner,
    restoreClient: async () => {
      const restored = await inner.restoreClient();
      if (restored === undefined) return undefined;
      return {
        ...restored,
        clientState: {
          ...restored.clientState,
          queries: restored.clientState.queries.filter(
            (query) => !NEVER_PERSISTED_ROUTES.has(query.queryKey[1])
          ),
        },
      };
    },
  };
}

type KeyValueStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/**
 * Empty a client's memory so that no reader on screen keeps what it held.
 *
 * `removeQueries` alone is not enough: it deletes the query objects and tells
 * nobody, so a mounted reader keeps its last data on screen until something
 * re-renders it, and a reader built anew with `keepPreviousData` gets that data
 * back as its placeholder. On desktop a signed-out private route stays mounted
 * under the auth gate, so that data stays in the DOM. `Query.reset` aborts the
 * query's fetch in flight, drops its data and tells every reader. Queries no
 * one reads are then removed; the ones on screen stay, empty, so a later
 * refetch (`revalidateApiCache` at sign-in) still reaches their readers.
 *
 * `refetch-on-screen` is for a new signed-in owner: what is on screen is asked
 * again, with the token of whoever Firebase holds now. `refetch-public-on-screen`
 * is for a session that ended: a public page on screen (desktop keeps it under
 * the auth gate) is asked again so it does not stay empty, and a private one
 * stays empty, since without a session its refetch can only fail. Every caller
 * runs after Firebase has dropped the session (`firebaseLogout` awaits
 * `auth.signOut()`, `adoptCacheOwner(null)` runs on Firebase's own report), so
 * the public refetch carries no token: a public route behind `optionalAuth`
 * answers as for anyone.
 */
export function emptyQueryMemory(
  client: QueryClient,
  then: "refetch-on-screen" | "refetch-public-on-screen"
): void {
  notifyManager.batch(() => {
    for (const query of client.getQueryCache().findAll()) query.reset();
  });
  client.removeQueries({ type: "inactive" });
  switch (then) {
    case "refetch-on-screen":
      void client.refetchQueries({ type: "active" });
      return;
    case "refetch-public-on-screen":
      void client.refetchQueries({
        type: "active",
        predicate: (query) => {
          // `["api", path, …]`: the path the query reads.
          const path = query.queryKey[1];
          return (
            typeof path === "string" && !path.startsWith("/webapp/private/")
          );
        },
      });
      return;
    default:
      throw new Error(then satisfies never);
  }
}

const CACHE_OWNER_KEY = "query-cache-owner";
const PAYLOAD_KEY = "REACT_QUERY_OFFLINE_CACHE";

/**
 * The characters of query state the payload may hold. Nothing is ever
 * garbage-collected (`gcTime: Infinity` in `queryClient.ts`) and each restore is
 * written back, so without a cap the payload grows with every article,
 * collection and feed page a reader opens until it fills the origin's
 * localStorage (5 MB in WebKit). Every other store then fails to write: an
 * iOS reader could no longer open a collection. WebKit counts up to 2 bytes per
 * character, so this keeps the payload under 2 MB.
 */
export const PERSISTED_QUERIES_BUDGET = 1_000_000;

/**
 * The newest queries that fit the budget: the oldest go first. A query that
 * does not fit is skipped, not a stop: a long-scrolled feed alone can pass the
 * budget, and the small ones after it (the profile the boot reads) still fit.
 */
function withinBudget(client: PersistedClient): PersistedClient {
  const newestFirst = [...client.clientState.queries].sort(
    (a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt
  );
  const kept: typeof newestFirst = [];
  let size = 0;
  for (const query of newestFirst) {
    const querySize = JSON.stringify(query).length;
    if (size + querySize > PERSISTED_QUERIES_BUDGET) continue;
    size += querySize;
    kept.push(query);
  }
  return { ...client, clientState: { ...client.clientState, queries: kept } };
}

// Read back from localStorage: a boundary, so it is parsed, never trusted.
const cacheOwnerSchema = z.object({ uid: z.string().nullable() });
// Every payload also carries the owner of the snapshot it holds, in the same
// storage item as the snapshot, so the two can never be written apart.
const PAYLOAD_OWNER_FIELD = "cacheOwner";
const payloadOwnerSchema = z.object({
  [PAYLOAD_OWNER_FIELD]: z.string().nullable(),
});

/**
 * The persisted cache belongs to ONE user, and no other may see it.
 *
 * Every persisted body is that user's: their conversations, their feed, their
 * saved papers, their rooms. Logout wipes it (`firebaseLogout`), but a session
 * can also end, or change hands, without one: an invalid credential, a token
 * lost at boot, a session restored for someone else, another tab signing in.
 * On a shared workstation the next reader is a different person, and a
 * persisted body renders before any refetch can replace it.
 *
 * Two owners make that impossible:
 * - the RECORDED owner, shared by every tab through storage: whose payload the
 *   disk holds. No key at all means a payload written before owners were
 *   recorded, by someone unknown; it is discarded on restore.
 * - the TAB owner, this tab's own: whose data its memory holds.
 *
 * `adoptCacheOwner` runs when auth resolves, before the app may render private
 * pages, and wipes whatever belongs to someone else. A write lands only when the
 * snapshot it carries belongs to the recorded owner: the throttled write of a
 * snapshot taken before a wipe, or another tab still holding the previous
 * user's memory, is refused instead of putting that user back on disk.
 *
 * Known and accepted: the boot skeleton reads the persisted user for its shape
 * (`getCachedBootDestination`) before auth resolves, so the previous owner's
 * pinned-room silhouette can show during the boot gate. Layout only, no data.
 */
export function createOwnedPersistence(deps: {
  storage: KeyValueStorage;
  queryClient: QueryClient;
}): {
  persister: Persister;
  adoptCacheOwner: (uid: string | null) => Promise<void>;
} {
  const recordedOwner = ():
    { recorded: false } | { recorded: true; uid: string | null } => {
    const raw = deps.storage.getItem(CACHE_OWNER_KEY);
    if (raw === null) return { recorded: false };
    const parsed = cacheOwnerSchema.safeParse(parseJson(raw));
    if (parsed.success) return { recorded: true, uid: parsed.data.uid };
    // Corrupt: whose payload it is cannot be known, exactly as with no record.
    // Throwing here would stop every sign-in on this device for good, since the
    // key outlives the page.
    Sentry.captureException(parsed.error, {
      tags: { source: "query-cache-owner", failure: "corrupt-record" },
    });
    return { recorded: false };
  };
  const recordOwner = (uid: string | null) => {
    try {
      deps.storage.setItem(CACHE_OWNER_KEY, JSON.stringify({ uid }));
    } catch (error: unknown) {
      // A full or refused storage. The previous record stays, and no write
      // from this tab matches it, so nothing is persisted until a record
      // succeeds: closed, not open. Sign-in itself must not wait on it.
      Sentry.captureException(error, {
        tags: { source: "query-cache-owner", failure: "record-refused" },
      });
    }
  };
  const isRecordedOwner = (uid: string | null) => {
    const owner = recordedOwner();
    return owner.recorded && owner.uid === uid;
  };

  // Whose data this tab's memory holds. Nothing private before the restore:
  // the restore sets it to the owner of the payload it hydrates.
  let tabOwner: string | null = null;
  // The session this tab has adopted, once auth has resolved.
  let session:
    { kind: "unresolved" } | { kind: "resolved"; uid: string | null } = {
    kind: "unresolved",
  };
  // Whose data the snapshot the throttled write will store belongs to. The
  // throttle stores the LATEST snapshot it was handed, taken from this tab's
  // memory when it was handed over.
  let snapshotOwner: string | null = null;

  const guarded: KeyValueStorage = {
    getItem: (key) => deps.storage.getItem(key),
    removeItem: (key) => {
      deps.storage.removeItem(key);
    },
    setItem: (key, value) => {
      if (!isRecordedOwner(snapshotOwner)) return;
      deps.storage.setItem(key, value);
    },
  };
  const inner = createSyncStoragePersister({
    storage: guarded,
    key: PAYLOAD_KEY,
    // The snapshot the throttle stores is the one `snapshotOwner` was stamped
    // for: both are the latest handed over.
    serialize: (client) =>
      JSON.stringify({
        ...withinBudget(client),
        [PAYLOAD_OWNER_FIELD]: snapshotOwner,
      }),
  });

  const persister: Persister = {
    persistClient: (client) => {
      snapshotOwner = tabOwner;
      return inner.persistClient(client);
    },
    restoreClient: async () => {
      // A payload no owner was recorded for came from before this rule: whose it
      // is cannot be known, so it never comes back.
      const owner = recordedOwner();
      if (!owner.recorded) {
        await inner.removeClient();
        return undefined;
      }
      // Another tab recorded someone else after this tab's session resolved:
      // that payload is theirs, and no later adoption here would wipe it.
      if (session.kind === "resolved" && session.uid !== owner.uid) {
        return undefined;
      }
      const restored = await inner.restoreClient();
      if (restored === undefined) return undefined;
      // The payload must be the recorded owner's by its own account. Tabs share
      // the storage but not a lock: another tab can check the record, then
      // write after a change of owner moved it (between the payload removal
      // and the new record, or later). Such a payload names its real owner,
      // so it is dropped here, whatever the order of the writes.
      const stamp = payloadOwnerSchema.safeParse(restored);
      if (!stamp.success || stamp.data[PAYLOAD_OWNER_FIELD] !== owner.uid) {
        await inner.removeClient();
        return undefined;
      }
      tabOwner = owner.uid;
      return restored;
    },
    removeClient: () => inner.removeClient(),
  };

  // Auth can resolve several times in a row (and every mounted
  // `useFirebaseUser` reports it): changes are applied one after the other.
  // A change that failed still lets the next one run; its caller gets the
  // failure.
  let pending: Promise<void> = Promise.resolve();
  const adoptCacheOwner = (uid: string | null) => {
    const change = pending.then(async () => {
      // Memory: emptied unless it is this user's, or a signed-out reader's
      // (public data, the same for everyone). Resetting a query aborts its fetch
      // in flight, so the previous owner's late answer cannot land anywhere.
      if (tabOwner !== null && tabOwner !== uid) {
        emptyQueryMemory(
          deps.queryClient,
          uid === null ? "refetch-public-on-screen" : "refetch-on-screen"
        );
      }
      tabOwner = uid;
      session = { kind: "resolved", uid };
      // Disk: emptied unless it is already this user's (another tab may have
      // recorded them first). The payload goes first: on a full storage, that
      // is what makes room for the record.
      if (!isRecordedOwner(uid)) {
        await inner.removeClient();
        recordOwner(uid);
      }
    });
    pending = change.catch(() => undefined);
    return change;
  };

  return { persister, adoptCacheOwner };
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const owned = createOwnedPersistence({
  storage: globalThis.localStorage,
  queryClient,
});

export const localStoragePersister = withoutNeverPersistedRoutes(
  owned.persister
);

/** See `createOwnedPersistence`. Awaited before auth is published. */
export const adoptCacheOwner = owned.adoptCacheOwner;

/**
 * Free the room the persisted cache takes, for a write that found localStorage
 * full. The cache is the one item the app can lose: it only spares a reload its
 * loading state, and the next change writes it again within its budget.
 */
export function dropPersistedQueryCache(): void {
  globalThis.localStorage.removeItem(PAYLOAD_KEY);
}
